/**
 * «Поток ордеров» — авторский профиль по настоящим сделкам.
 *
 * Отличие от «Профиля объёма» не в оформлении, а в данных. Тот берёт
 * свечи и размазывает объём бара по его диапазону поровну — это
 * прикидка. Этот берёт сделки Binance и Bybit: известно, на какой цене
 * прошла каждая и кто был инициатором. Отсюда то, чего прикидка дать не
 * может: покупки и продажи на уровне, перевес, откуда пришёл объём.
 *
 * Сделки биржи назад не отдают, поэтому история потока начинается с
 * того дня, когда сервер начал их слушать. Граница видна на графике и
 * названа в табло: пустота левее неё — «не слушали», а не «не торговали».
 */

import type { DrawnProfile } from '../chart/order-flow-layer.js';
import { volumeOf } from '../orderflow/profile.js';
import {
  VENUE_TITLE,
  flowSettings,
  money,
  priceText,
  signedMoney,
  type FlowProfileSettings,
} from '../orderflow/settings.js';
import type { ReadoutLegendItem, ReadoutRow } from './overlays.js';
import { boolParam, type IndicatorDefinition } from './types.js';

const RANGE_TITLE: Record<FlowProfileSettings['range'], string> = {
  visible: 'видимая часть',
  day: 'текущие сутки (UTC)',
  '1H': 'последний час',
  '4H': 'последние 4 часа',
  '24H': 'последние 24 часа',
  bars: 'последние свечи',
};

const MODE_TITLE: Record<FlowProfileSettings['mode'], string> = {
  volume: 'объём',
  bidask: 'покупки и продажи',
  delta: 'перевес',
};

const LEGEND: readonly ReadoutLegendItem[] = [
  {
    sample: 'soft',
    term: 'Полосы справа',
    text: 'Сколько наторговали на каждой цене. Длиннее полоса — больше денег прошло через этот уровень.',
  },
  {
    sample: 'strong',
    term: 'POC — главный уровень',
    text: 'Цена, где торговали больше всего. К ней рынок часто возвращается: там больше всего участников и их позиций.',
  },
  {
    sample: 'dashed',
    term: 'VAH и VAL — зона стоимости',
    text: 'Верх и низ участка, где прошло 70% объёма (долю можно поменять). Внутри — «справедливая» цена, за её краями рынок ищет новую.',
  },
  {
    sample: 'faded',
    term: 'Бледные полосы',
    text: 'Уровни вне зоны стоимости: там торговали меньше, и цена проходит их быстрее.',
  },
];

function kilo(value: number): string {
  return money(value);
}

function dateText(at: number): string {
  return new Date(at).toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
}

/** Одна фраза о том, где цена относительно зоны и кто активнее. */
function plainWords(drawn: DrawnProfile, close: number): string {
  const { profile } = drawn;
  const total = profile.buy + profile.sell;
  const buyShare = total > 0 ? profile.buy / total : 0.5;
  const who =
    buyShare >= 0.55
      ? 'покупали по рынку заметно активнее, чем продавали'
      : buyShare <= 0.45
        ? 'продавали по рынку заметно активнее, чем покупали'
        : 'покупки и продажи почти уравновешены';

  if (profile.valueHigh === null || profile.valueLow === null) return `За отрезок ${who}.`;
  const where =
    close > profile.valueHigh
      ? 'Цена выше зоны, где торговали больше всего, — рынок пробует более высокие цены'
      : close < profile.valueLow
        ? 'Цена ниже зоны, где торговали больше всего, — рынок пробует более низкие цены'
        : 'Цена внутри зоны, где торговали больше всего, — там, где рынок считает цену справедливой';
  return `${where}. За отрезок ${who}.`;
}

export const ORDER_FLOW: IndicatorDefinition = {
  id: 'order-flow',
  name: 'Поток ордеров',
  description:
    'Профиль по настоящим сделкам Binance и Bybit: сколько купили и продали на каждой цене, ' +
    'где главный уровень и зона стоимости. Наведите на полосу — покажет цифры по биржам.',
  author: 'specter',
  placement: 'overlay',
  orderFlow: true,
  params: [
    {
      key: 'mode',
      label: 'Что показывать',
      hint:
        'Объём — сколько всего наторговали на цене. Покупки и продажи — продажи влево, покупки вправо: видно, кто был активнее на каждом уровне. Перевес — разница между ними: зелёный — покупателей больше, красный — продавцов.',
      type: 'select',
      default: 'volume',
      options: [
        { value: 'volume', label: 'Объём' },
        { value: 'bidask', label: 'Покупки и продажи' },
        { value: 'delta', label: 'Перевес' },
      ],
    },
    {
      key: 'range',
      label: 'За какой отрезок',
      hint:
        'Видимая часть пересчитывается при прокрутке и масштабе — самый удобный вариант. Остальные держат профиль за фиксированное время, как бы вы ни двигали график. Это не то же самое, что таймфрейм свечей.',
      type: 'select',
      default: 'visible',
      options: [
        { value: 'visible', label: 'Видимая часть' },
        { value: 'day', label: 'Текущие сутки' },
        { value: '1H', label: 'Последний час' },
        { value: '4H', label: 'Последние 4 часа' },
        { value: '24H', label: 'Последние 24 часа' },
        { value: 'bars', label: 'Последние N свечей' },
      ],
    },
    {
      key: 'bars',
      label: 'Свечей в отрезке',
      hint: 'Сколько последних свечей брать, если выбран отрезок «Последние N свечей».',
      type: 'number',
      default: 100,
      min: 5,
      max: 1500,
      step: 5,
    },
    {
      key: 'source',
      label: 'Биржи',
      hint:
        'Обе — общий объём рынка, так уровни видны лучше всего. Одна биржа — если нужно понять, где именно торговали. В подсказке к уровню разбивка по биржам есть всегда.',
      type: 'select',
      default: 'all',
      options: [
        { value: 'all', label: 'Binance и Bybit' },
        { value: 'binance', label: 'Только Binance' },
        { value: 'bybit', label: 'Только Bybit' },
      ],
    },
    {
      key: 'unit',
      label: 'Считать в',
      hint: 'Доллары удобнее сравнивать между монетами. Монеты — привычнее, если вы думаете в BTC или ETH.',
      type: 'select',
      default: 'usd',
      options: [
        { value: 'usd', label: 'Долларах' },
        { value: 'coin', label: 'Монетах' },
      ],
    },
    {
      key: 'poc',
      label: 'Главный уровень (POC)',
      hint: 'Линия на цене, где торговали больше всего. Рынок часто к ней возвращается.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'pocHistory',
      label: 'Как сдвигался главный уровень',
      hint:
        'Линия по свечам: где был главный уровень от начала отрезка до каждой свечи. Ровная — рынок принимает цену, ступеньками — ищет новую.',
      type: 'boolean',
      default: false,
    },
    {
      key: 'valueLines',
      label: 'Границы зоны стоимости (VAH/VAL)',
      hint: 'Пунктир сверху и снизу участка, где прошла основная часть объёма.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'valueArea',
      label: 'Зона стоимости, % объёма',
      hint:
        'Какая доля всего объёма входит в зону. 70% — общепринятое значение. Больше — зона шире и спокойнее, меньше — уже и строже.',
      type: 'number',
      default: 70,
      min: 10,
      max: 95,
      step: 5,
    },
    {
      key: 'side',
      label: 'С какой стороны',
      hint:
        'Справа профиль стоит рядом с текущей ценой — так привычнее. Слева он не спорит с таблицами индикаторов, которые тоже живут справа.',
      type: 'select',
      default: 'right',
      options: [
        { value: 'right', label: 'Справа' },
        { value: 'left', label: 'Слева' },
      ],
    },
    {
      key: 'width',
      label: 'Ширина профиля, % графика',
      hint: 'Насколько далеко влево тянутся полосы. Это про вид, на расчёт не влияет.',
      type: 'number',
      default: 25,
      min: 5,
      max: 60,
      step: 1,
    },
    {
      key: 'table',
      label: 'Таблица на графике',
      hint:
        'Короткая сводка: главный уровень, зона стоимости и кто активнее. Таблица стоит в правом углу и может закрыть профиль — поэтому выключена: главные цены и так подписаны на графике.',
      type: 'boolean',
      default: false,
    },
    {
      key: 'details',
      label: 'Подробная таблица',
      hint:
        'Добавляет в таблицу, сколько всего наторговали, откуда пришёл объём и вывод простыми словами. Таблица стоит справа, там же, где профиль, — поэтому по умолчанию она короткая.',
      type: 'boolean',
      default: false,
    },
    { key: 'buyColor', label: 'Цвет покупок', hint: 'Каким цветом рисовать покупки и перевес в сторону покупателей.', type: 'color', default: '#16C784' },
    { key: 'sellColor', label: 'Цвет продаж', hint: 'Каким цветом рисовать продажи и перевес в сторону продавцов.', type: 'color', default: '#EA3943' },
    { key: 'volumeColor', label: 'Цвет объёма', hint: 'Основной цвет полос в режиме «Объём» — спокойный, чтобы не спорить со свечами.', type: 'color', default: '#7C8DB5' },
    { key: 'pocColor', label: 'Цвет главного уровня', hint: 'Цвет линии и полосы главного уровня: он должен находиться глазом первым.', type: 'color', default: '#F5A623' },
    { key: 'valueColor', label: 'Цвет зоны стоимости', hint: 'Цвет полос внутри зоны стоимости и её границ.', type: 'color', default: '#5B8DEF' },
  ],
  outputs: [],
  warmup: () => 0,
  calc: () => ({}),
  shapes: (_candles, p, context) => {
    const data = context.orderFlow;
    if (!data) return [];
    return [{ kind: 'flow-profile', data, settings: flowSettings(p), key: JSON.stringify(p) }];
  },
  readout: (candles, p, context) => {
    if (!boolParam(p, 'table', true)) return [];
    const data = context.orderFlow;
    if (!data) return [];
    if (data.error) return [{ label: 'Поток', value: data.error, tone: 'muted', full: true }];
    if (!data.loaded) return [{ label: 'Поток', value: 'загружаю…', tone: 'muted' }];

    const settings = flowSettings(p);
    const details = boolParam(p, 'details', false);
    const rows: ReadoutRow[] = [];
    const since = [...settings.venues]
      .map((venue) => data.since[venue])
      .filter((value): value is number => value !== undefined)
      .sort((a, b) => a - b)[0];

    const drawn = data.drawn(JSON.stringify(p)) as DrawnProfile | undefined;
    const profile = drawn?.profile;
    const last = candles[candles.length - 1];

    if (details) {
      rows.push({
        label: 'Отрезок',
        value: `${RANGE_TITLE[settings.range]}${profile ? ` · ${profile.bars} свеч.` : ''}`,
        tone: 'muted',
      });
    }

    if (!drawn || !profile || !last) {
      // Слой ещё не рисовал: табло считается раньше первого кадра.
      rows.push({ label: 'Профиль', value: 'считаю…', tone: 'muted' });
    } else if (profile.rows.length === 0) {
      rows.push({ label: 'Сделок в отрезке', value: 'нет', tone: 'muted' });
    } else {
      const total = profile.buy + profile.sell;
      const buyShare = total > 0 ? (profile.buy / total) * 100 : 50;
      const step = data.step * drawn.group;

      if (profile.poc) {
        rows.push({
          label: 'Главный уровень',
          value: priceText((profile.poc.low + profile.poc.high) / 2, step),
        });
      }
      if (profile.valueLow !== null && profile.valueHigh !== null) {
        rows.push({
          label: 'Зона стоимости',
          value: `${priceText(profile.valueLow, step)} – ${priceText(profile.valueHigh, step)}`,
        });
      }
      rows.push({
        label: 'Покупки / продажи',
        value: `${Math.round(buyShare)}% / ${Math.round(100 - buyShare)}%`,
        tone: buyShare > 52 ? 'up' : buyShare < 48 ? 'down' : undefined,
        meter: buyShare,
      });
      rows.push({
        label: 'Перевес',
        value: signedMoney(profile.buy - profile.sell),
        tone: profile.buy >= profile.sell ? 'up' : 'down',
      });
      if (details) rows.push({ label: 'Всего наторговали', value: kilo(total) });

      // Доли бирж — по всем строкам профиля: откуда пришёл объём.
      const byVenue = new Map<string, number>();
      for (const row of profile.rows) {
        for (const [venue, share] of row.byVenue) {
          byVenue.set(venue, (byVenue.get(venue) ?? 0) + volumeOf(share));
        }
      }
      if (details && byVenue.size > 1) {
        rows.push({
          label: 'Откуда объём',
          value: [...byVenue]
            .sort((a, b) => b[1] - a[1])
            .map(([venue, value]) => `${VENUE_TITLE[venue as keyof typeof VENUE_TITLE]} ${Math.round((value / total) * 100)}%`)
            .join(' · '),
        });
      }

      if (profile.gaps.size > 0) {
        rows.push({
          label: 'Связь',
          value: `обрывалась: ${[...profile.gaps].map((venue) => VENUE_TITLE[venue]).join(', ')}`,
          tone: 'down',
        });
      }
      // Вывод словами — первым, как у всех индикаторов: цифры ниже его подтверждают.
      rows.unshift({ label: 'Что происходит', value: plainWords(drawn, last.close), full: true });
    }

    /*
     * С какого дня поток — только когда это что-то меняет.
     *
     * Если отрезок целиком после начала сбора, дата ничего не говорит.
     * Если начинается раньше — профиль неполный, и это надо сказать.
     */
    if (since === undefined) {
      rows.push({ label: 'Собирается с', value: 'только начали', tone: 'muted' });
    } else if (details || (drawn && drawn.from < since)) {
      rows.push({ label: 'Собирается с', value: dateText(since), tone: 'muted' });
    }
    return rows;
  },
  legend: LEGEND,
  label: (p) => `Поток ордеров · ${MODE_TITLE[flowSettings(p).mode]}`,
};
